/**
 * HUD Layout — root overlay container.
 *
 * Renders all 5 HUD zones as `position: fixed` layers over the 3D canvas:
 *   - Top-left:    BrandButton (project logo + back)
 *   - Top-center:  HotspotNavBar (hidden when no hotspots)
 *   - Bottom-center: MainToolbar (cinematic + tools + nav modes)
 *   - Bottom-right: InfoPill (VR + scene info)
 *
 * Also renders scene-level overlays:
 *   - LoadingOverlay (while status === 'loading')
 *   - ErrorOverlay   (while status === 'error')
 *   - SceneInfoPanel (when sceneInfoOpen === true)
 *
 * Mobile:
 *   On screens ≤ 768px wide, renders <MobileHUDLayout /> instead of the
 *   desktop layout (implementation deferred; current stub returns null).
 */
import { useEffect, useState } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import { useRaceTrackStore } from '@/modules/race-track';
import type { VrQualityPreset } from '@mindcloudxai_3d/core';
import { BrandButton } from './BrandButton';
import { MainToolbar } from './MainToolbar';
import { InfoPill } from './InfoPill';
import { SceneInfoPanel } from './SceneInfoPanel';
import { MobileHUDLayout } from './MobileHUDLayout';
import { LoadingOverlay } from '../viewport/LoadingOverlay';
import { ErrorOverlay, type ErrorKind } from '../viewport/ErrorOverlay';
import { SharePanel } from '../share/SharePanel';
import { HotspotInfoCard } from '../hotspots/HotspotInfoCard';
import { HotspotNavBar } from '../hotspots/HotspotNavBar';
import { MobileGameController } from '../navigation/MobileWalkController';
import { AssetPanel } from '@modules/asset-library/AssetPanel';

// ---------------------------------------------------------------------------
// Mobile detection hook
// ---------------------------------------------------------------------------

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// ---------------------------------------------------------------------------
// Error kind resolver
// ---------------------------------------------------------------------------

function resolveErrorKind(message: string | null): ErrorKind {
  if (!message) return 'unknown';
  const m = message.toLowerCase();
  if (m.includes('preview') || m.includes('token')) return 'token-invalid';
  if (m.includes('offline') || m.includes('taken offline') || m.includes('no splat'))
    return 'scene-offline';
  if (m.includes('network') || m.includes('fetch') || m.includes('axios')) return 'network';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// HUDLayout
// ---------------------------------------------------------------------------

interface HUDLayoutProps {
  /** Optional retry handler passed down to ErrorOverlay. */
  onRetry?: () => void;
  onToggleCinematic: () => void;
  onEnterVr?: () => boolean;
  onSelectVrQuality?: (preset: VrQualityPreset) => void;
  /** 获取当前渲染控制器（移动端漫游控制器使用） */
  getController?: () => import('@renderer/index').RendererController | null;
}

export function HUDLayout({
  onRetry,
  onToggleCinematic,
  onEnterVr,
  onSelectVrQuality,
  getController,
}: HUDLayoutProps) {
  const status = useViewerStore((s) => s.status);
  const errorMessage = useViewerStore((s) => s.errorMessage);
  const immersive = useViewerStore((s) => s.immersive);
  const hasScene = useViewerStore((s) => s.sceneConfig !== null);
  const isMobile = useIsMobile();
  const isEditing = useRaceTrackStore((s) => s.isEditing);

  // -- Loading / Error overlays (always rendered regardless of mobile) ------
  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <div className="pointer-events-none absolute inset-0 z-30">
      {/* Loading overlay */}
      {isLoading && <LoadingOverlay />}

      {/* Error overlay */}
      {isError && (
        <ErrorOverlay
          kind={resolveErrorKind(errorMessage)}
          message={errorMessage ?? undefined}
          onRetry={onRetry}
        />
      )}

      {/* Scene info panel (overlaid, needs pointer-events) */}
      {hasScene && (
        <div className="pointer-events-auto">
          <SceneInfoPanel />
          <SharePanel />
          <HotspotInfoCard />
        </div>
      )}

      {/* HUD — 沉浸模式下淡出，恢复时淡入 */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          immersive ? 'pointer-events-none opacity-0' : ''
        }`}
      >
        {isMobile ? (
          <MobileHUDLayout onToggleCinematic={onToggleCinematic} />
        ) : (
          <div className="pointer-events-none absolute inset-0">
            {/* Top-left: Brand/Back */}
            <div className="pointer-events-auto absolute top-2 left-2">
              <BrandButton />
            </div>

            {/* Top-center: Hotspot nav */}
            <div className="pointer-events-auto absolute top-2 left-1/2 -translate-x-1/2">
              <HotspotNavBar />
            </div>

            {/* Bottom-center: Main toolbar — 编辑时或无场景时隐藏 */}
            {!isEditing && hasScene && (
              <div className="pointer-events-auto absolute bottom-4 left-1/2 -translate-x-1/2">
                <MainToolbar onToggleCinematic={onToggleCinematic} />
              </div>
            )}

            {/* Bottom-right: Info pill — 无场景时隐藏 */}
            {hasScene && (
              <div className="pointer-events-auto absolute right-4 bottom-4">
                <InfoPill onEnterVr={onEnterVr} onSelectVrQuality={onSelectVrQuality} />
              </div>
            )}

            {/* Asset Library panel (right side, pointer-events-auto) */}
            {getController && <AssetPanel getController={getController} />}
          </div>
        )}
      </div>

      {/* Joystick 不受沉浸模式影响，始终可操作 */}
      {isMobile && getController && <MobileJoystickLayer getController={getController} />}
    </div>
  );
}

/** 移动端漫游摇杆：独立于沉浸淡出容器，fly/walk/avatar 模式常驻显示 */
function MobileJoystickLayer({
  getController,
}: {
  getController: () => import('@renderer/index').RendererController | null;
}) {
  const navMode = useViewerStore((s) => s.navigationMode);
  const isGaming = navMode === 'fly' || navMode === 'walk' || navMode === 'avatar';
  if (!isGaming) return null;
  return <MobileGameController getController={getController} />;
}
